import React from 'react';
import {
  Box,
  makeStyles,
  Theme,
  createStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';
import { FixedSizeList as List, ListChildComponentProps } from 'react-window';
import { ChannelsSidebarProps } from './ChannelsSidebar';
import StandardPage from '../UI/StandardPage';
import { RootState } from '../../store/reduxTypes';
import { setSecondarySidebarHidden } from '../../store/ui/actions';
import {
  LinkUrlListWrapper,
  SelectorWrapper,
} from '../ListWrappers/LinkUrlListWrapper';
import { itemHeight } from '../UI';
import ChannelsSidebarV2 from './ChannelsSidebar.v2';
import { ChannelSidebarItemV2 } from './ChannelSidebarItem.v2';

export interface BaseChannelFormData {
  fields: {
    membersType: string;
    memberIDs: string[];
  };
}

export interface ItemRenderProps<T> {
  channelOption: T;
  onSelect: (channelData: T) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    list: {
      position: 'relative',
      // we should use important here otherwise it cannot be override
      [theme.breakpoints.up('sm')]: {
        overflowY: 'hidden !important',
        '&:hover': {
          overflowY: 'overlay !important',
        },
        '@supports ( -moz-appearance:none )': {
          scrollbarColor: 'gray',
          scrollbarWidth: 'thin',
        },
      },
      [theme.breakpoints.down('xs')]: {
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      },
    },
  }),
);

interface BaseChannelSidebarProps extends ChannelsSidebarProps {
  sidebarItemOptions: any;
  standardPageParams: any;
  onSelectChannel: (sidebarItemOption: any) => void;
  isChannelRootSelected?: (sidebarItemOption: any) => boolean;
  shouldSetQueryParam?: boolean;
  title?: string;
  modalTitle?: string;
  noCreateAction?: boolean;
  // this will help in rendering the list with independent styling
  itemRenderer?: (props: ItemRenderProps<unknown>) => any;
  noSpace?: boolean;
  // if true disable the default behaviour of selecting the first channel of sidebar
  disableDefaultSelect?: boolean;
}

const BaseChannelSidebar: React.FC<BaseChannelSidebarProps> = ({
  onCreate,
  initialCreateFormValue,
  entity,
  sidebarItemOptions,
  standardPageParams,
  onSelectChannel,
  isChannelRootSelected,
  shouldSetQueryParam = true,
  title = '',
  modalTitle = '',
  noCreateAction = false,
  itemRenderer: ItemRenderer,
  noSpace = false,
  disableDefaultSelect = false,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const theme = useTheme();
  const isSmallWidth = useMediaQuery(theme.breakpoints.down('xs'), {
    noSsr: true,
  });

  /**
   * when compoennt is mounted/unmountd change the redux store
   * to track if secondary sidebar is hidden or not
   */
  React.useEffect(() => {
    dispatch(setSecondarySidebarHidden(false));

    return () => {
      dispatch(setSecondarySidebarHidden(true));
    };
  }, []);

  const {
    isClient,
    selectedResponseId,
    fileSelectedChannelId,
    selectedExtensionItemId,
  } = useSelector(
    (state: RootState) => ({
      isClient: state.user.isClient,
      selectedResponseId: state.intakeForms.selectedResponseId,
      fileSelectedChannelId: state.files.selectedChannel?.id,
      selectedExtensionItemId: state.dashboard.selectedItemId,
    }),
    shallowEqual,
  );

  const channelSidebarItemRenderer = (props: ListChildComponentProps) => {
    const { index, style, data } = props;
    const { onSelectCallback } = data;
    const channelOption = sidebarItemOptions[index];

    let isSelected = false;
    switch (entity) {
      case 'fileChannel':
        isSelected = fileSelectedChannelId === channelOption.fileChannel.id;
        break;
      default:
        isSelected =
          selectedExtensionItemId === channelOption.id ||
          selectedResponseId === channelOption.id;
    }

    // check if root channel is selected using callback prop,
    // if no callback prop then root is selected by default
    const isRootSelected = isChannelRootSelected
      ? isChannelRootSelected(channelOption)
      : true;

    // if we have ItemRender component use it to render the list
    // else use the default channel sidebar item
    if (ItemRenderer) {
      return (
        <ItemRenderer
          channelOption={channelOption}
          onSelect={onSelectCallback}
          key={`${entity}-channel-${index}`}
          style={style} // these styles are applied to make sidebar scrolling smooth
        />
      );
    }

    return (
      <ChannelSidebarItemV2
        channelType={entity}
        channelOption={channelOption}
        style={style}
        onSelect={onSelectCallback}
        isSelected={isSelected}
        isRootSelected={isRootSelected}
        key={`${entity}-channel-${index}`}
      />
    );
  };

  const sidebarContentRenderer = () => (
    <AutoSizer>
      {({ height, width }: Size) => (
        <LinkUrlListWrapper
          fallbackIndex={!isSmallWidth ? 0 : null}
          channelType={entity}
          onSelect={onSelectChannel}
          data={sidebarItemOptions}
          shouldSetQueryParam={shouldSetQueryParam}
          disableDefaultSelect={disableDefaultSelect}
          // eslint-disable-next-line react/no-unstable-nested-components
          component={(
            onSelectWrapper: SelectorWrapper,
            refList: React.RefObject<any>,
          ) => (
            <List
              ref={refList}
              className={classes.list}
              height={height}
              width={width}
              itemSize={itemHeight + 8}
              itemCount={sidebarItemOptions.length}
              itemData={{
                onSelectCallback: onSelectWrapper,
              }}
            >
              {channelSidebarItemRenderer}
            </List>
          )}
        />
      )}
    </AutoSizer>
  );

  const sidebarContent = () => {
    if (sidebarItemOptions.length > 0) {
      return sidebarContentRenderer();
    }
    if (!isClient && isSmallWidth) {
      return <StandardPage {...standardPageParams} />;
    }
    return null;
  };

  return (
    <ChannelsSidebarV2
      entity={entity}
      onCreate={onCreate}
      initialCreateFormValue={initialCreateFormValue}
      title={title}
      modalTitle={modalTitle}
      noCreateAction={noCreateAction}
      noSpace={noSpace}
    >
      <Box flex="1 1 auto" display="flex" data-testid={`${entity}-sidebar`}>
        {sidebarContent()}
      </Box>
    </ChannelsSidebarV2>
  );
};

export default BaseChannelSidebar;
